export type MetroStepState = "completed" | "active" | "pending" | "failed";

export interface MetroStepDef {
  key: string; // unique step key
  labelKey: string; // display label
  isConditional?: boolean; // only shown in certain statuses
}

export interface BulkMetroStageConfig {
  currentStepKey: string; // which step is "active" right now
  completedStepKeys: string[]; // steps already done
  failedStepKeys?: string[]; // steps that failed
  showProcessed?: boolean; // whether to show the final processed step
  approvalLabel?: string; // override the approval step label
}

// ---------------------------------------------------------------------------
// STEP DEFINITIONS (order matters - top to bottom = timeline order)
// ---------------------------------------------------------------------------
export const BULK_METRO_STEPS: MetroStepDef[] = [
  { key: "initiated", labelKey: "initiated" },
  { key: "approval", labelKey: "approval" },
  { key: "processed", labelKey: "processed", isConditional: true },
];

// ---------------------------------------------------------------------------
// STATUS -> STAGE CONFIG MAP
// ---------------------------------------------------------------------------
// Pattern mirrors STATUS_CONFIG: one flat map, keyed by numeric jobStatusCode.
// Adding a new status = add one entry here. No component changes needed.
// ---------------------------------------------------------------------------
export const BULK_METRO_STATUS_MAP: Record<number, BulkMetroStageConfig> = {
  // === Upload / Received / Submitted stages ================================================
  4500: { currentStepKey: "initiated", completedStepKeys: [] },
  7500: { currentStepKey: "initiated", completedStepKeys: [] },
  4511: { currentStepKey: "initiated", completedStepKeys: [] },
  4551: { currentStepKey: "initiated", completedStepKeys: [] },
  4513: { currentStepKey: "initiated", completedStepKeys: [] },
  4620: { currentStepKey: "initiated", completedStepKeys: [] },

  // === Ready for Review ==============================================================================
  4510: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    approvalLabel: "Approval In Progress",
    showProcessed: false,
  },
  7510: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    approvalLabel: "Approval In Progress",
    showProcessed: false,
  },

  // === Awaiting Approval ==============================================================================
  4515: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    approvalLabel: "Approval In Progress",
    showProcessed: false,
  },
  7515: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    approvalLabel: "Approval In Progress",
    showProcessed: false,
  },
  4415: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    approvalLabel: "Approval In Progress",
    showProcessed: false,
  },
  7415: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    approvalLabel: "Approval In Progress",
    showProcessed: false,
  },

  // === Pending with Bank / Pending Upload ===================================================
  4450: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7450: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  4600: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7600: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },

  // === In Process =======================================================================================
  4550: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7550: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },

  // === Future Dated ====================================================================================
  4580: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7580: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    approvalLabel: "Approved",
    showProcessed: true,
  },

  // === Fully Processed / Completed ===============================================================
  // File Received / File Submitted are post-approval, terminal-success statuses,
  // so all 3 steps (initiated, approval, processed) render as completed/green,
  // same as the other fully-processed codes below.
  4670: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  4640: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7670: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7640: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  4590: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  4440: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7440: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval", "processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },

  // === Rejected / Failed ==============================================================================
  4585: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    failedStepKeys: ["approval"],
    approvalLabel: "Approval Rejected",
    showProcessed: false,
  },
  7585: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    failedStepKeys: ["approval"],
    approvalLabel: "Approval Rejected",
    showProcessed: false,
  },
  4425: {
    currentStepKey: "approval",
    completedStepKeys: ["initiated"],
    failedStepKeys: ["approval"],
    approvalLabel: "Rejected by Approver",
    showProcessed: false,
  },
  4430: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    failedStepKeys: ["processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  7430: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    failedStepKeys: ["processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
  4685: {
    currentStepKey: "processed",
    completedStepKeys: ["initiated", "approval"],
    failedStepKeys: ["processed"],
    approvalLabel: "Approved",
    showProcessed: true,
  },
};

// export type MetroStepState = "completed" | "active" | "pending" | "failed";

// export interface MetroStepDef {
//   key: string; // unique step key
//   labelKey: string; // display label
//   isConditional?: boolean; // only shown in certain statuses
// }

// export interface BulkMetroStageConfig {
//   currentStepKey: string; // which step is "active" right now
//   completedStepKeys: string[]; // steps already done
//   failedStepKeys?: string[]; // steps that failed
//   showProcessed?: boolean; // whether to show the final processed step
//   approvalLabel?: string; // override the approval step label
// }

// // ---------------------------------------------------------------------------
// // STEP DEFINITIONS (order matters - top to bottom = timeline order)
// // ---------------------------------------------------------------------------
// export const BULK_METRO_STEPS: MetroStepDef[] = [
//   { key: "initiated", labelKey: "initiated" },
//   { key: "approval", labelKey: "approval" },
//   { key: "processed", labelKey: "processed", isConditional: true },
// ];

// // ---------------------------------------------------------------------------
// // STATUS -> STAGE CONFIG MAP
// // ---------------------------------------------------------------------------
// // Pattern mirrors STATUS_CONFIG: one flat map, keyed by numeric jobStatusCode.
// // Adding a new status = add one entry here. No component changes needed.
// // ---------------------------------------------------------------------------
// export const BULK_METRO_STATUS_MAP: Record<number, BulkMetroStageConfig> = {
//   // === Upload / Received / Submitted stages ================================================
//   4500: { currentStepKey: "initiated", completedStepKeys: [] },
//   7500: { currentStepKey: "initiated", completedStepKeys: [] },
//   4511: { currentStepKey: "initiated", completedStepKeys: [] },
//   4551: { currentStepKey: "initiated", completedStepKeys: [] },
//   4513: { currentStepKey: "initiated", completedStepKeys: [] },
//   4620: { currentStepKey: "initiated", completedStepKeys: [] },
//   4640: { currentStepKey: "initiated", completedStepKeys: [] },
//   4670: { currentStepKey: "initiated", completedStepKeys: [] },
//   7670: { currentStepKey: "initiated", completedStepKeys: [] },

//   // === Ready for Review ==============================================================================
//   4510: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     approvalLabel: "Approval In Progress",
//     showProcessed: false,
//   },
//   7510: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     approvalLabel: "Approval In Progress",
//     showProcessed: false,
//   },

//   // === Awaiting Approval ==============================================================================
//   4515: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     approvalLabel: "Approval In Progress",
//     showProcessed: false,
//   },
//   7515: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     approvalLabel: "Approval In Progress",
//     showProcessed: false,
//   },
//   4415: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     approvalLabel: "Approval In Progress",
//     showProcessed: false,
//   },
//   7415: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     approvalLabel: "Approval In Progress",
//     showProcessed: false,
//   },

//   // === Pending with Bank / Pending Upload ===================================================
//   4450: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   7450: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   4600: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   7600: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },

//   // === In Process =======================================================================================
//   4550: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   7550: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },

//   // === Future Dated ====================================================================================
//   4580: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   7580: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },

//   // === Fully Processed / Completed ===============================================================
//   4590: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval", "processed"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   4440: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval", "processed"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   7440: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval", "processed"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },

//   // === Rejected / Failed ==============================================================================
//   4585: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     failedStepKeys: ["approval"],
//     approvalLabel: "Approval Rejected",
//     showProcessed: false,
//   },
//   4425: {
//     currentStepKey: "approval",
//     completedStepKeys: ["initiated"],
//     failedStepKeys: ["approval"],
//     approvalLabel: "Rejected by Approver",
//     showProcessed: false,
//   },
//   4430: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     failedStepKeys: ["processed"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   7430: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     failedStepKeys: ["processed"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
//   4685: {
//     currentStepKey: "processed",
//     completedStepKeys: ["initiated", "approval"],
//     failedStepKeys: ["processed"],
//     approvalLabel: "Approved",
//     showProcessed: true,
//   },
// };
